import type { GameState } from '../types';
import { exportGridBase64, importGridBase64 } from '../terrain/grid';
import { getRNGState, setRNGState } from '../terrain/rng';

/**
 * FNV-1a hash — fast, good distribution, no dependencies.
 * Returns a hex string.
 */
function fnv1a(str: string): string {
    let hash = 0x811c9dc5; // FNV offset basis
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = (hash * 0x01000193) | 0; // FNV prime, keep 32-bit
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Round a number to 2 decimal places for cross-engine consistency. */
function r2(n: number): number {
    return Math.round(n * 100) / 100;
}

/**
 * Build the raw hash parts array from game-critical state fields.
 * Used by hashGameState() for hashing, and by NetDebugLog for field-level diffing.
 *
 * Intentionally omits:
 * - Visual-only fields (rotation, squash, climbDustCooldown, etc.)
 * - Timers that are visual (dialogTimer, gameOverTimer)
 * - Menu/pause state
 */
export function getHashParts(state: GameState): string[] {
    const parts: string[] = [];

    // Core state — only fields both clients agree on deterministically.
    // EXCLUDED: frameCount (offset by network latency), freezeUntil (frame-relative),
    // turnTimer (timing-sensitive, ~0 at boundary anyway).
    parts.push(`p:${state.currentPlayer}`);
    parts.push(`sp:${state.startingPlayer}`);
    parts.push(`ph:${state.phase}`);
    parts.push(`t:${state.turnCount}`);
    parts.push(`w:${r2(state.wind)}`);
    parts.push(`vy:${r2(state.voidY)}`);
    parts.push(`vty:${r2(state.voidTargetY)}`);
    // voidMood: hashed for divergence detection, but intentionally NOT in turn_snapshot.
    // Mood decays in advanceTurn() — same pattern as wind/voidY/voidTargetY.
    // Syncing via snapshot would cause double-decay. Full resync corrects divergence.
    parts.push(`vm:${state.voidMood}`);
    parts.push(`rn:${state.roundNumber}`);
    parts.push(`tn:${state.turnNumber}`);
    parts.push(`gl:${state.activeGlitch}`);

    // Players — game-critical fields only
    for (let i = 0; i < state.players.length; i++) {
        const p = state.players[i];
        // hitstunFrames excluded — frame-relative, diverges with network latency.
        // pendingDeath excluded — transient flag consumed within same turn.
        // selectedShotTier excluded — cosmetic HUD highlight synced via real-time aim_update,
        // not in turn_snapshot. armedShotTier (included) is what gates firing.
        parts.push(`P${i}:${r2(p.x)},${r2(p.y)},${r2(p.vx)},${r2(p.vy)},${r2(p.damagePercent)},${r2(p.hp)},${p.alive ? 1 : 0},${r2(p.momentum)},${p.tumble ? 1 : 0},${p.settled ? 1 : 0},${p.fastFallActive ? 1 : 0},${p.liftActive ? 1 : 0},${p.platformDropTimer},${p.onWireframe ? 1 : 0},${p.armedShotTier ?? 0},${r2(p.weaponCharge)},${p.dashImmune ? 1 : 0},${p.stocks},${p.respawnInvulnFrames},${p.cupidMarkTurns},${p.cupidMarkedBy}`);
    }

    // Projectiles (if any active)
    for (let pi = 0; pi < state.projectiles.length; pi++) {
        const pj = state.projectiles[pi];
        parts.push(`proj${pi}:${r2(pj.x)},${r2(pj.y)},${r2(pj.vx)},${r2(pj.vy)},${pj.bouncesRemaining}`);
    }

    // Grapple state
    parts.push(`ga:${state.grapple.ammo.join(',')}`);
    if (state.grapple.active) {
        const g = state.grapple.active;
        parts.push(`grp:${g.playerIndex},${g.phase},${r2(g.anchorX)},${r2(g.anchorY)}`);
        if (g.hook) {
            parts.push(`hook:${r2(g.hook.x)},${r2(g.hook.y)}`);
        }
    }

    // DI state — timer excluded (timing-sensitive, DI resolves before turn boundary)
    if (state.diActivePlayer >= 0) {
        parts.push(`di:${state.diActivePlayer}`);
    }

    // Burst state — timer excluded (frame-relative, diverges between clients)
    if (state.burstActivePlayer >= 0) {
        parts.push(`bu:${state.burstActivePlayer}`);
    }

    // Void retaliation
    if (state.voidRetaliationQueue.length > 0) {
        parts.push(`vr:${state.voidRetaliationQueue.length}`);
    }

    // Vortex holes
    if (state.vortexHoles.length > 0) {
        for (let i = 0; i < state.vortexHoles.length; i++) {
            const h = state.vortexHoles[i];
            const cdParts = Object.entries(h.tentacleCooldowns)
                .filter(([, v]) => v > 0)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([k, v]) => `${k}:${v}`)
                .join(';');
            parts.push(`vh${i}:${r2(h.x)},${r2(h.y)},${h.ownerIndex},${h.turnsRemaining},${cdParts}`);
        }
    }

    // Vortex collapse (physics gate — controls whether tickCollapse runs)
    if (state.collapseActive) {
        parts.push(`ca:1`);
    }

    // Terrain seed (terrain is deterministic from this)
    parts.push(`ts:${state.terrainSeed}`);
    // Stage ID (fixed stage selection)
    parts.push(`si:${state.stageId}`);

    return parts;
}

/**
 * Hash game-critical state fields for desync detection.
 * Returns an FNV-1a hex string.
 */
export function hashGameState(state: GameState): string {
    return fnv1a(getHashParts(state).join('|'));
}

/**
 * Serialize game state for resync snapshot.
 * Includes all state needed to reconstruct the game, including
 * the terrain grid (base64-encoded) so craters are preserved.
 */
export function serializeState(state: GameState): string {
    // Shallow-safe: GameState contains only plain objects, numbers, strings, arrays
    // The only tricky field is projectile.weapon (WeaponDef reference) — serialize as weapon name
    const { multiplayer: _mp, ...stateWithoutMp } = state;
    const serializable = {
        ...stateWithoutMp,
        // Convert Map to plain object for JSON serialization
        defensePreFreezeStates: Object.fromEntries(state.defensePreFreezeStates),
        terrainGrid: exportGridBase64(), // include actual terrain with craters
        rngState: getRNGState(), // include seeded RNG internal state for resync
        projectiles: state.projectiles.map(pj => ({
            ...pj,
            weapon: pj.weapon.id, // serialize as weapon id
            trail: [], // don't send trail points
            visualState: undefined, // visual-only, not synced
        })),
        deferredSpawns: state.deferredSpawns.map(ds => ({
            ...ds,
            projectile: {
                ...ds.projectile,
                weapon: ds.projectile.weapon.id,
                trail: [],
                visualState: undefined,
            },
        })),
        // Don't send grapple rope particles (visual only, reconstructable)
        grapple: {
            ...state.grapple,
            active: state.grapple.active
                ? {
                    ...state.grapple.active,
                    rope: [], // visual only
                    ropeRestLengths: [], // visual only
                }
                : null,
        },
    };
    return JSON.stringify(serializable);
}

/**
 * Deserialize a resync snapshot back into GameState.
 * Requires the weapon registry to restore WeaponDef references.
 * Also restores the terrain grid from the snapshot.
 */
export function deserializeState(
    json: string,
    weaponLookup: (name: string) => import('../weapons').WeaponDef,
): Partial<GameState> {
    const parsed = JSON.parse(json);

    // Restore terrain grid (includes all craters)
    if (parsed.terrainGrid) {
        importGridBase64(parsed.terrainGrid);
        delete parsed.terrainGrid; // not a GameState field
    }

    // Restore seeded RNG state
    if (typeof parsed.rngState === 'number') {
        setRNGState(parsed.rngState);
        delete parsed.rngState; // not a GameState field
    }

    // Restore weapon references on projectiles
    if (parsed.projectiles && Array.isArray(parsed.projectiles)) {
        for (const pj of parsed.projectiles) {
            if (typeof pj.weapon === 'string') {
                pj.weapon = weaponLookup(pj.weapon);
            }
        }
    }

    // Restore weapon references on deferred spawns
    if (parsed.deferredSpawns && Array.isArray(parsed.deferredSpawns)) {
        for (const ds of parsed.deferredSpawns) {
            if (ds.projectile && typeof ds.projectile.weapon === 'string') {
                ds.projectile.weapon = weaponLookup(ds.projectile.weapon);
            }
        }
    }

    // Restore Map from plain object
    if (parsed.defensePreFreezeStates && typeof parsed.defensePreFreezeStates === 'object' && !(parsed.defensePreFreezeStates instanceof Map)) {
        parsed.defensePreFreezeStates = new Map(
            Object.entries(parsed.defensePreFreezeStates).map(([k, v]) => [Number(k), v]),
        );
    }

    return parsed as Partial<GameState>;
}
